import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useMe } from "@/api/auth";
import { useCategoryDetail } from "@/api/menus";
import type { FieldItem, ServiceItem } from "@/api/catalog";
import {
  newUuid,
  useCreateOrder,
  usePayOrder,
} from "@/api/orders";
import { payBulkWithTotp, payWithTotp } from "@/api/pay_helpers";
import type { ApiError } from "@/api/client";
import { useTotpStatus } from "@/api/totp";
import { useT } from "@/lib/i18n";
import { useToasts } from "@/store/toasts";

/**
 * Category drill-down. Each row is a self-contained mini-form: quantity
 * input + price + per-row Buy. The bottom bar buys everything with
 * `qty > 0` in one click - orders are placed sequentially so a
 * per-service failure doesn't block the rest, and each result lands as
 * its own toast (with a "View →" link to the order detail page).
 */

const COMMON_FIELD_KEYS = new Set(["quantity", "qty", "amount"]);

function pickQtyField(fields: FieldItem[]): FieldItem | null {
  // Prefer an explicit `quantity`/`amount` numeric field; fall back to
  // any single numeric field; otherwise null (the BUY column will be
  // hidden).
  const named = fields.find(
    (f) =>
      COMMON_FIELD_KEYS.has(f.key.toLowerCase()) &&
      (f.type === "int" || f.type === "float"),
  );
  if (named) return named;
  const numeric = fields.filter((f) => f.type === "int" || f.type === "float");
  if (numeric.length === 1) return numeric[0];
  return null;
}

function defaultMin(field: FieldItem): number {
  return field.min ?? (field.type === "int" ? 1 : 0.01);
}

export function CategoryDetail() {
  const { id } = useParams<{ id: string }>();
  const categoryId = id ? Number(id) : null;
  const cat = useCategoryDetail(categoryId);
  const create = useCreateOrder();
  const pay = usePayOrder();
  const pushToast = useToasts((s) => s.push);
  const me = useMe();
  const totp = useTotpStatus();
  const t = useT();
  const purchaseNeedsTotp = () =>
    !!me.data &&
    !!totp.data?.enabled &&
    !!totp.data?.require_purchase;

  // Per-service quantity buffer.
  const [qty, setQty] = useState<Record<number, number>>({});
  const [busy, setBusy] = useState<Record<number, boolean>>({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const qtyField = useMemo(
    () => (cat.data ? pickQtyField(cat.data.fields) : null),
    [cat.data],
  );

  const setSvcQty = (svc_id: number, raw: string) => {
    if (!qtyField) return;
    if (raw === "") {
      setQty((c) => ({ ...c, [svc_id]: 0 }));
      return;
    }
    const n =
      qtyField.type === "int" ? parseInt(raw, 10) : parseFloat(raw);
    if (!Number.isFinite(n) || n < 0) return;
    setQty((c) => ({ ...c, [svc_id]: n }));
  };

  const total = useMemo(() => {
    if (!cat.data) return 0;
    let sum = 0;
    for (const svc of cat.data.services) {
      const n = qty[svc.service_id] ?? 0;
      if (n > 0) sum += svc.price * n;
    }
    return sum;
  }, [cat.data, qty]);

  const totalSelected = useMemo(
    () =>
      Object.values(qty).reduce((acc, n) => (n > 0 ? acc + 1 : acc), 0),
    [qty],
  );

  if (cat.isLoading) return <div className="text-slate-400">{t("common.loading")}</div>;
  if (cat.isError || !cat.data) {
    return (
      <div className="space-y-3">
        <Link to="/" className="text-sm text-accent">
          {t("catalog.back")}
        </Link>
        <div className="text-sm text-slate-500">{t("menu.not_found")}</div>
      </div>
    );
  }
  const c = cat.data;

  const buyOne = async (svc: ServiceItem) => {
    if (!qtyField) return;
    const n = qty[svc.service_id] ?? 0;
    if (n <= 0) return;
    setBusy((b) => ({ ...b, [svc.service_id]: true }));
    try {
      const ok = await placeOrder(svc, n, qtyField.key);
      if (ok) setQty((cur) => ({ ...cur, [svc.service_id]: 0 }));
    } finally {
      setBusy((b) => ({ ...b, [svc.service_id]: false }));
    }
  };

  const buyAll = async () => {
    if (!qtyField) return;
    setBulkBusy(true);
    try {
      // Selected services in declared order. We use payBulkWithTotp
      // so a 2FA-gated user is prompted **once** and we reuse the
      // same code across every order in the batch.
      const selected = c.services
        .map((svc) => ({ svc, n: qty[svc.service_id] ?? 0 }))
        .filter((x) => x.n > 0);

      await payBulkWithTotp(
        selected,
        async ({ svc, n }, totpCode) => {
          const ok = await placeOrder(svc, n, qtyField.key, totpCode);
          if (ok) setQty((cur) => ({ ...cur, [svc.service_id]: 0 }));
          return ok;
        },
        purchaseNeedsTotp,
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const placeOrder = async (
    svc: ServiceItem,
    qtyValue: number,
    qtyKey: string,
    totpCode?: string,
  ): Promise<boolean> => {
    const cid = newUuid();
    try {
      await create.mutateAsync({
        service_id: svc.service_id,
        custom_id: cid,
        fields: [{ key: qtyKey, value: qtyValue }],
      });
      // Single-shot purchases prompt for TOTP via payWithTotp; the
      // bulk path passes the already-known `totpCode` straight in.
      const pres = totpCode
        ? await pay.mutateAsync({ custom_id: cid, totp_code: totpCode })
        : await payWithTotp(
            (code) =>
              pay.mutateAsync({
                custom_id: cid,
                ...(code ? { totp_code: code } : {}),
              }),
            { title: `Confirm purchase: ${svc.service_name}` },
          );
      if (pres === null) {
        pushToast({
          kind: "info",
          title: `Cancelled: ${svc.service_name}`,
        });
        return false;
      }
      pushToast({
        kind:
          pres.status === "completed" || pres.status === "in_progress"
            ? "success"
            : "info",
        title:
          pres.status === "completed"
            ? `Delivered: ${svc.service_name} × ${qtyValue}`
            : pres.status === "in_progress"
              ? `Queued: ${svc.service_name} × ${qtyValue}`
              : pres.status === "refunded"
                ? `Refunded: ${svc.service_name}`
                : `Insufficient balance for ${svc.service_name}`,
        body:
          pres.note ||
          (pres.balance ? `Balance: $${pres.balance}` : undefined),
        link: { to: `/orders/${cid}`, label: "View order" },
      });
      return true;
    } catch (err) {
      const e = err as ApiError;
      const detail =
        typeof e.detail === "string"
          ? e.detail
          : e.detail
            ? JSON.stringify(e.detail)
            : "Order failed";
      pushToast({
        kind: "error",
        title: `Failed: ${svc.service_name}`,
        body: detail,
      });
      return false;
    }
  };

  return (
    <div className="space-y-6 pb-32">
      <div>
        <Link
          to={c.menu_id ? `/menus/${c.menu_id}` : "/"}
          className="text-sm text-accent"
        >
          ← {c.menu_name || t("catalog.title")}
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-800">
          {c.category_name}
        </h1>
      </div>

      {!qtyField && c.fields.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {t("catalog.unsupported_fields")}
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-surface-muted text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-3 font-semibold sm:px-5">{t("catalog.col.service")}</th>
              <th className="px-3 py-3 font-semibold sm:px-5">{t("catalog.col.price")}</th>
              <th className="hidden px-3 py-3 font-semibold sm:table-cell sm:px-5">
                {t("catalog.col.in_stock")}
              </th>
              {qtyField && (
                <>
                  <th className="px-3 py-3 font-semibold sm:px-5">
                    {qtyField.name || t("catalog.col.qty")}
                  </th>
                  <th className="px-3 py-3 text-right font-semibold sm:px-5">
                    {t("catalog.col.action")}
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {c.services.map((svc) => {
              const out = svc.in_stock <= 0;
              const n = qty[svc.service_id] ?? 0;
              const max = qtyField?.max ?? Math.max(svc.in_stock, 1);
              return (
                <tr
                  key={svc.service_id}
                  className="border-t border-surface-border"
                >
                  <td className="px-3 py-3 font-medium text-slate-800 sm:px-5">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span>{svc.service_name}</span>
                      {/* service_id subtitle: clients used to copy this
                          out of the V1 Django UI when filing API orders. */}
                      <span className="font-mono text-[11px] text-slate-400">
                        #{svc.service_id}
                      </span>
                    </div>
                    {/* Stock hint inline on mobile (column hidden) */}
                    <div
                      className={`mt-0.5 text-xs sm:hidden ${
                        out ? "text-red-600" : "text-emerald-700"
                      }`}
                    >
                      {out
                        ? t("catalog.out_of_stock")
                        : `${svc.in_stock.toLocaleString()} ${t("catalog.in_stock")}`}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-700 sm:px-5">
                    {svc.price.toFixed(4)} {svc.currency}
                  </td>
                  <td
                    className={`hidden px-3 py-3 sm:table-cell sm:px-5 ${
                      out ? "text-red-600" : "text-emerald-700"
                    }`}
                  >
                    {out ? t("catalog.out") : svc.in_stock.toLocaleString()}
                  </td>
                  {qtyField && (
                    <>
                      <td className="px-3 py-3 sm:px-5">
                        <input
                          type="number"
                          inputMode={
                            qtyField.type === "int" ? "numeric" : "decimal"
                          }
                          step={qtyField.step ?? (qtyField.type === "float" ? 0.01 : 1)}
                          min={defaultMin(qtyField)}
                          max={max}
                          disabled={out}
                          value={n === 0 ? "" : String(n)}
                          onChange={(e) =>
                            setSvcQty(svc.service_id, e.target.value)
                          }
                          placeholder="0"
                          className="input w-20 sm:w-24"
                        />
                      </td>
                      <td className="px-3 py-3 text-right sm:px-5">
                        <button
                          onClick={() => buyOne(svc)}
                          disabled={out || n <= 0 || !!busy[svc.service_id]}
                          className="btn btn-primary text-xs"
                        >
                          {busy[svc.service_id] ? "..." : t("catalog.buy")}
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            {c.services.length === 0 && (
              <tr>
                <td colSpan={qtyField ? 5 : 3} className="px-5 py-6 text-center text-slate-400">
                  {t("catalog.no_services")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {qtyField && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-surface-border bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center justify-between gap-3 px-3 py-2 sm:gap-4 sm:px-6 sm:py-3">
            <div className="min-w-0 flex-1 text-xs text-slate-600 sm:text-sm">
              {totalSelected === 0 ? (
                <span className="hidden sm:inline">{t("catalog.bulk.set_qty")}</span>
              ) : (
                <>
                  <span className="font-medium text-slate-800">
                    {totalSelected}
                  </span>{" "}
                  {t("catalog.bulk.selected")} · {t("catalog.bulk.total")}{" "}
                  <span className="font-medium text-slate-800">
                    ${total.toFixed(4)}
                  </span>
                </>
              )}
            </div>
            <button
              className="btn btn-primary text-sm"
              disabled={totalSelected === 0 || bulkBusy}
              onClick={buyAll}
            >
              {bulkBusy ? t("catalog.buy_busy") : t("catalog.buy_all")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
